import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { serverTimestamp, collection, collectionData } from '@angular/fire/firestore'
import {
    Client,
    ContractStatus,
    Merchant,
    Parking,
    PayGateClient,
    PayGateResultCode,
    PaymentMethod,
    paymentMethodsPath,
    PaymentMethodStatus,
    Plan,
    Subscription,
    SubscriptionType,
    Term,
} from '@parkupp/core'
import { AdminService } from '../../../services/admin.service'
import * as moment from 'moment'
import * as firestore from 'firebase/firestore'
import { ActivatedRoute, Router } from '@angular/router'
import { MerchantService } from '../../../services/merchant.service'
import { ParkingService } from '../../../services/parking.service'
import { ClientService } from '../../../services/client.service'
import { SubscriptionService } from '../../../services/subscription.service'
import { PaymentService } from '../../../services/payment.service'
import { AuthenticationService } from '../../../services/authentication.service'
import { Subscription as RxSubscription } from 'rxjs'

@Component({
    selector: 'app-admin-subscription-create-on-behalf',
    templateUrl: './admin-subscription-create-on-behalf.component.html',
    styleUrls: ['./admin-subscription-create-on-behalf.component.scss'],
})
export class AdminSubscriptionCreateOnBehalfComponent implements OnInit, OnDestroy {
    formData: FormGroup
    formLoading = false
    formError = ''

    subscription: Subscription
    merchants: Merchant[]
    parkingLookup: Parking[]
    clientLookup: Client[]

    parkingKey: string
    merchantKey: string
    clientKey: string

    selectedClient: Client | null = null
    clientEmail = ''
    clientPhone = ''

    availablePaymentMethods: PaymentMethod[] = []
    selectedPaymentMethod: PaymentMethod | null = null
    paymentMethodsLoaded = false
    private paymentMethodsSub: RxSubscription | null = null

    constructor(
        private router: Router,
        private formBuilder: FormBuilder,
        private adminService: AdminService,
        private activatedRoute: ActivatedRoute,
        private merchantService: MerchantService,
        private parkingService: ParkingService,
        private clientService: ClientService,
        private subscriptionService: SubscriptionService,
        private paymentService: PaymentService,
        private authenticationService: AuthenticationService
    ) {
        const routeClientKey = this.activatedRoute.snapshot.params['clientKey']
        if (routeClientKey) {
            this.clientKey = routeClientKey
        }

        this.formData = this.formBuilder.group({
            startDate: [moment().add(1, 'M').startOf('month').format('yyyy-MM-DD'), Validators.required],
            endDate: [moment().add(1, 'M').endOf('month').format('yyyy-MM-DD'), []],
            baysBooked: [1, [Validators.required, Validators.min(1)]],
            feeInRands: [null, Validators.required],
            contractLength: [1, [Validators.required, Validators.min(1)]],
            parkingName: [''],
            clientName: [''],
            confirmMerchantCompanyName: [''],
            confirmClientEmail: [''],
            confirmClientPhone: [''],
            confirmMonthlyCost: [''],
        })

        this.formData.controls['contractLength'].valueChanges.subscribe((value) =>
            this.formChanges({ contractLength: value })
        )
        this.formData.controls['startDate'].valueChanges.subscribe((value) => this.formChanges({ startDate: value }))

        this.subscription = new Subscription()

        this.merchantService.list().subscribe((merchants) => {
            this.merchants = merchants
        })
        this.parkingService.list().subscribe((parkings) => {
            this.parkingLookup = parkings
        })
        this.clientService.list().subscribe((clients) => {
            this.clientLookup = clients

            if (this.clientKey && !this.selectedClient) {
                const preselected = clients.find((c) => c.$key === this.clientKey)
                if (preselected) {
                    this.formData.controls['clientName'].setValue(`${preselected.firstName} ${preselected.lastName || ''}`.trim())
                    this.onClientSelect(preselected)
                }
            }
        })
    }

    ngOnInit(): void {}

    ngOnDestroy(): void {
        if (this.paymentMethodsSub) {
            this.paymentMethodsSub.unsubscribe()
        }
    }

    formChanges(changes: { contractLength?: number; startDate?: number }) {
        const contractLength = changes.contractLength ? changes.contractLength : this.formData.value.contractLength
        const startDate = changes.startDate ? changes.startDate : this.formData.value.startDate

        if (changes.contractLength || changes.startDate) {
            this.updateEndDate(Term.MONTHLY, startDate, contractLength)
        }

        const bays = this.formData.value.baysBooked
        const overrideCost = this.formData.value.feeInRands
        this.formData.controls['confirmMonthlyCost'].setValue(bays * overrideCost)
    }

    updateEndDate(term: string, startDate: Date, contractLength: any) {
        let lengthUnit: moment.unitOfTime.DurationConstructor = 'M'
        if (term == Term.DAILY || term == Term.ONCEOFF) {
            lengthUnit = 'd'
        }

        const newEndDate = moment(startDate).add(contractLength, lengthUnit).subtract(1, 'day')
        this.formData.controls['endDate'].setValue(newEndDate.format('yyyy-MM-DD'))
    }

    onParkingSelect(selectedParking: Parking): void {
        this.parkingKey = selectedParking.$key!
        this.merchantKey = selectedParking.merchantRef.id

        this.formData.controls['confirmMerchantCompanyName'].setValue(selectedParking.denormMerchantCompanyName)
        if (selectedParking.plans && selectedParking.plans[0]) {
            this.formData.controls['feeInRands'].setValue(selectedParking.plans[0].feeInCents / 100)
        }

        this.subscription.parkingRef = this.parkingService.getDocRef(this.parkingKey) as any
        this.subscription.denormParkingTitle = selectedParking.title

        this.subscription.merchantRef = this.merchantService.getDocRef(this.merchantKey) as any
        this.subscription.denormMerchantCompanyName = selectedParking.denormMerchantCompanyName

        if (selectedParking.denormEnterpriseKey) {
            this.subscription.denormEnterpriseKey = selectedParking.denormEnterpriseKey
        }

        if (selectedParking.isMonthToMonth) {
            this.subscription.isMonthToMonth = true
            this.formData.controls['contractLength'].setValue(1)
            this.formData.controls['contractLength'].disable()
        } else {
            this.subscription.isMonthToMonth = false
            this.formData.controls['contractLength'].enable()
        }
    }

    onClientSelect(selectedClient: Client): void {
        this.clientKey = selectedClient.$key!
        this.selectedClient = selectedClient

        this.clientService.adminGetEmailFromClientKey(selectedClient.$key!).subscribe((privateClient) => {
            if (privateClient) {
                this.clientEmail = privateClient.email
                this.clientPhone = privateClient.phoneNumber
                this.formData.controls['confirmClientEmail'].setValue(privateClient.email)
                this.formData.controls['confirmClientPhone'].setValue(privateClient.phoneNumber)
            }
        })

        this.subscription.clientRef = this.clientService.getDocRef(selectedClient.$key!) as any
        this.subscription.denormClientFirstName = selectedClient.firstName
        this.subscription.denormClientLastName = selectedClient.lastName

        this.loadPaymentMethods(selectedClient.$key!)
    }

    private loadPaymentMethods(clientKey: string): void {
        if (this.paymentMethodsSub) {
            this.paymentMethodsSub.unsubscribe()
            this.paymentMethodsSub = null
        }

        this.paymentMethodsLoaded = false
        this.availablePaymentMethods = []
        this.selectedPaymentMethod = null

        const paymentMethodsRef = collection(this.clientService.firestore, paymentMethodsPath(clientKey))
        this.paymentMethodsSub = collectionData(paymentMethodsRef, { idField: 'documentId' }).subscribe(
            (documents: any[]) => {
                const cards: PaymentMethod[] = []
                let primaryCard: PaymentMethod | null = null

                documents.forEach((documentData) => {
                    const paymentMethod = new PaymentMethod(documentData)
                    paymentMethod.$key = documentData.documentId

                    if (paymentMethod.status === PaymentMethodStatus.active) {
                        cards.push(paymentMethod)
                        if (paymentMethod.primary) {
                            primaryCard = paymentMethod
                        }
                    }
                })

                this.availablePaymentMethods = cards
                this.selectedPaymentMethod = primaryCard || cards[0] || null
                this.paymentMethodsLoaded = true
            }
        )
    }

    selectPaymentMethod(paymentMethod: PaymentMethod): void {
        this.selectedPaymentMethod = paymentMethod
    }

    get hasCardOnFile(): boolean {
        return this.paymentMethodsLoaded && this.availablePaymentMethods.length > 0
    }

    get noCardOnFile(): boolean {
        return this.paymentMethodsLoaded && this.availablePaymentMethods.length === 0 && !!this.clientKey
    }

    get canSubmit(): boolean {
        return (
            this.formData.valid &&
            !this.formLoading &&
            !!this.parkingKey &&
            !!this.clientKey &&
            !!this.selectedPaymentMethod
        )
    }

    private async resolveCreatedByAdmin(): Promise<{ adminKey: string; name?: string; email?: string } | null> {
        const adminKey = this.authenticationService.uid
        if (!adminKey) {
            return null
        }

        const result: { adminKey: string; name?: string; email?: string } = { adminKey }

        const email = this.authenticationService.email
        if (email) {
            result.email = email
        }

        try {
            const adminProfile = await this.clientService.getClientByUid(adminKey)
            if (adminProfile) {
                const name = `${adminProfile.firstName || ''} ${adminProfile.lastName || ''}`.trim()
                if (name) {
                    result.name = name
                }
            }
        } catch (e) {
            console.warn('Could not resolve admin profile for audit log:', e)
        }

        return result
    }

    private getPayGateErrorMessage(resultCode: PayGateResultCode | undefined): string | null {
        if (!resultCode) return null

        const errorMessages: { [key: number]: string } = {
            [PayGateResultCode['Call for Approval']]: 'The card requires bank approval. Ask the client to contact their bank.',
            [PayGateResultCode['Card Expired']]: 'The card has expired. The client must add a new card.',
            [PayGateResultCode['Insufficient Funds']]: 'Insufficient funds on the card.',
            [PayGateResultCode['Invalid Card Number']]: 'Invalid card number on file.',
            [PayGateResultCode['Bank Interface Timeout']]: 'Bank connection timeout. Please try again.',
            [PayGateResultCode['Invalid Card']]: 'Invalid card on file. Ask the client to add a new card.',
            [PayGateResultCode['Declined']]: 'The card was declined. Ask the client to contact their bank or add a new card.',
            [PayGateResultCode['Lost Card']]: 'This card has been reported lost.',
            [PayGateResultCode['Invalid Card Length']]: 'Invalid card number length.',
            [PayGateResultCode['Suspected Fraud']]: 'Transaction declined for security reasons.',
            [PayGateResultCode['Card Reported as Stolen']]: 'This card has been reported stolen.',
            [PayGateResultCode['Restricted Card']]: 'This card has been restricted by the bank.',
            [PayGateResultCode['Excessive Card Usage']]: 'Too many transactions on this card. Try again later.',
            [PayGateResultCode['Card Blacklisted']]: 'This card cannot be used.',
            [PayGateResultCode['Declined authentication failed']]: 'Card authentication failed.',
            [PayGateResultCode['Auth Declined']]: 'Authorization declined.',
            [PayGateResultCode['3D Secure Lookup Timeout']]: '3D Secure verification timed out.',
            [PayGateResultCode['Invalid expiry date']]: 'Invalid expiry date on the stored card.',
            [PayGateResultCode['Invalid Amount']]: 'Invalid payment amount.',
        }

        return errorMessages[resultCode] || null
    }

    async onSubmit() {
        this.formError = ''

        if (!this.canSubmit) {
            return
        }

        if (!this.selectedClient || !this.selectedPaymentMethod) {
            this.formError = 'Missing client or payment method.'
            return
        }

        this.formLoading = true

        try {
            this.subscription.type = SubscriptionType.PAYGATE
            this.subscription.createdAt = serverTimestamp() as firestore.Timestamp
            this.subscription.updatedAt = serverTimestamp() as firestore.Timestamp
            this.subscription.baysBooked = this.formData.value.baysBooked
            this.subscription.startDate = firestore.Timestamp.fromDate(new Date(this.formData.value.startDate))
            this.subscription.endDate = firestore.Timestamp.fromDate(new Date(this.formData.value.endDate))
            this.subscription.status = ContractStatus.new
            this.subscription.period =
                this.formData.getRawValue().contractLength || this.formData.value.contractLength

            if (this.subscription.period > 1) {
                const startDate = new Date(this.formData.value.startDate)
                this.subscription.nextDebitDate = firestore.Timestamp.fromDate(startDate)
            }

            this.subscription.plan = new Plan({
                feeInCents: this.formData.value.feeInRands * 100,
                term: Term.MONTHLY,
            })

            this.subscription.payGateClient = new PayGateClient({
                firstName: this.selectedClient.firstName,
                lastName: this.selectedClient.lastName,
                email: this.clientEmail,
                mobile: this.clientPhone,
                key: this.selectedClient.$key,
            })

            const createdByAdmin = await this.resolveCreatedByAdmin()
            if (createdByAdmin) {
                this.subscription.createdByAdmin = createdByAdmin
            }

            const createResponse = await this.subscriptionService.create(this.subscription)
            if (createResponse.status !== 'success') {
                this.formError = 'Failed to create the subscription. Please try again.'
                this.formLoading = false
                return
            }

            const subscriptionKey = createResponse.subscriptionReference

            const paymentResponse: any = await this.paymentService.httpProcessTokenPaymentCreateSubscription(
                this.selectedPaymentMethod,
                subscriptionKey
            )

            if (paymentResponse?.error) {
                const errorMessage =
                    this.getPayGateErrorMessage(paymentResponse.payGateResponse?.resultCode) ||
                    paymentResponse.errorMessage ||
                    paymentResponse.payGateResponse?.resultDescription ||
                    'Could not submit the request. Please review the subscription in the admin panel.'
                this.formError = errorMessage
                this.formLoading = false
                await this.router.navigate([`/admin/subscription/${subscriptionKey}`])
                return
            }

            this.formLoading = false
            await this.router.navigate([`/admin/subscription/${subscriptionKey}`])
        } catch (error: any) {
            console.error('Admin create-on-behalf failed:', error)
            this.formError =
                error?.message || 'An unexpected error occurred while creating the subscription. Please try again.'
            this.formLoading = false
        }
    }
}
